// ============================================================
// useTaskAutoSave.ts
// Per-task debounced auto-save for the task EDIT form.
//
// Mirrors useWitnessAutoSave / useCaseDeadlineAutoSave verbatim in shape:
// tasks being edited accumulate a partial payload per taskId, debounce 1s,
// and fire one PATCH when idle. NOT a React hook and NO store subscription —
// a save fires ONLY when the edit form calls scheduleTaskSave from an
// onChange. (New-task CREATION stays commit-on-submit in TasksView — there's
// no id to autosave against until the task exists.)
//
// This is STEP 1 of task live-sync: per-field autosave gives every task edit a
// server updatedAt (advanced into taskBase) + a 409 concurrency guard, which
// the live-sync engine (step 2, applyRemoteTaskEdit) will build on.
//
// Public API:
//   scheduleTaskSave(taskId, changes) — debounce 1s, accumulate changes
//       across fields, fire one PATCH when idle.
//   flushTaskAutoSave()               — fire EVERY pending timer immediately
//       and await them. Call before navigating away from the edit form.
//   cancelTaskSave(taskId)            — drop the pending timer + payload for
//       one task (e.g. on delete, so we don't PATCH a row we just removed).
//
// Saves go through the store's updateTask action (not updateTaskApi directly)
// so the cached tasks[] list stays in sync with the server response — the
// task list reflects edits the moment they persist.
// ============================================================
import type { CreateTaskPayload, Task } from '../types/tasks.types'
import {
  getTaskBaseUpdatedAt, setTaskBaseUpdatedAt,
  getTaskBaseValues, setTaskBaseValues,
} from './saveBaseVersion'
import { serverChangedTouchedColumns, MAX_409_REAPPLY_ATTEMPTS } from './reconcile409'
import { getTaskByIdApi } from '../api/casevoApi'
import { useCasevoStore } from '../store/useCasevoStore'

// The lightweight display fields the TASK LIST renders — broadcast firm-wide on
// every task save so other users' lists patch the row live (see sendTaskListUpdate).
export const taskListFields = (t: Task): Record<string, unknown> => ({
  title: t.title,
  status: t.status,
  dueDate: t.dueDate,
  assignedToUserId: t.assignedToUserId,
})

const DEBOUNCE_MS = 1000

// Collab (Yjs) columns on a task: `description` is now a passive HTML MIRROR
// whose merge is owned by Yjs (doc `task:<id>:description`, Stage 4). A 409
// caused solely by such a column must NEVER toast — the next snapshot carries
// the merged HTML. Mirrors CASE_COLLAB_COLUMNS / WITNESS_COLLAB_COLUMNS.
const TASK_COLLAB_COLUMNS = new Set<string>(['description'])

// Module-scope state. Same singleton pattern as useWitnessAutoSave but keyed
// by taskId so each edited task debounces independently.
const timers = new Map<string, ReturnType<typeof setTimeout>>()
// Accumulated partial — every field change merges into here. When the timer
// fires we PATCH with whatever has built up, then clear.
const pendingPayloads = new Map<string, Partial<CreateTaskPayload>>()
// Tasks whose last save failed (non-409) — drives the shared save-error state,
// cleared once that task next saves successfully. Mirrors useWitnessAutoSave.
const taskErrored = new Set<string>()

const runTaskSave = async (
  taskId: string,
  // Column-aware 409 recovery attempt counter (0 = first try).
  attempt: number = 0,
): Promise<void> => {
  const payload = pendingPayloads.get(taskId)
  if (!payload || Object.keys(payload).length === 0) return
  // Move the payload into a local before the await — if more changes land
  // during the request they start a new pending entry rather than getting
  // clobbered by this run's PATCH return.
  pendingPayloads.delete(taskId)
  try {
    // Optimistic-concurrency: attach the SERVER updatedAt captured when the
    // edit form opened so the backend can 409 on a stale write. Opt-in —
    // omit if no base was captured.
    const base = getTaskBaseUpdatedAt(taskId)
    const body = base !== null ? { ...payload, baseUpdatedAt: base } : payload
    const updated = await useCasevoStore.getState().updateTask(taskId, body)
    // Advance the baseline to the server's new updatedAt.
    setTaskBaseUpdatedAt(taskId, updated.updatedAt)
    // Advance the column-aware base VALUES to the just-saved server row.
    setTaskBaseValues(taskId, updated as unknown as Record<string, unknown>)
    if (taskErrored.delete(taskId) && taskErrored.size === 0) useCasevoStore.getState().setSaveError(null)
    // (Stage 3) The firm-wide task LIST patch is now SERVER-driven: the
    // updateTask controller emits entity-changed op:'update'. No client-side
    // list-update broadcast here (useEntityChangeSync patches + advances base).
  } catch (err) {
    const resp = (err as { response?: { status?: number; data?: { currentUpdatedAt?: string } } }).response
    if (resp?.status === 409) {
      // COLUMN-AWARE recovery (mirrors useAutoSave): the backend guard is
      // timestamp-based, so this may be a SPURIOUS 409 — another user edited
      // DIFFERENT columns of this task. Re-fetch the row by id (getTaskByIdApi
      // — listTasksApi returns a scoped list that may omit this task) and, if
      // the server didn't change any of the columns THIS save touched (vs our
      // base values), silently re-apply on the fresh base — no toast. Only a
      // genuine same-column clash (or exhausted retries / failed re-fetch)
      // surfaces the conflict.
      const touched = Object.keys(payload)
      // Collab (Yjs) columns never drive a conflict — base both the overlap
      // decision and the toast on the NON-collab columns; an all-collab 409
      // (e.g. just the description mirror) resolves silently.
      const nonCollabTouched = touched.filter((c) => !TASK_COLLAB_COLUMNS.has(c))
      const baseValues = getTaskBaseValues(taskId)
      if (attempt < MAX_409_REAPPLY_ATTEMPTS && touched.length > 0 && baseValues) {
        try {
          const server = await getTaskByIdApi(taskId)
          const overlap = serverChangedTouchedColumns(
            nonCollabTouched, baseValues, server as unknown as Record<string, unknown>,
          )
          if (!overlap) {
            // Non-overlap: advance to the fresh server row and re-send ONLY our
            // columns. Re-stage the drained payload, MERGING any newer change
            // on top so we don't lose it.
            setTaskBaseUpdatedAt(taskId, server.updatedAt)
            setTaskBaseValues(taskId, server as unknown as Record<string, unknown>)
            const newer = pendingPayloads.get(taskId) ?? {}
            pendingPayloads.set(taskId, { ...payload, ...newer })
            await runTaskSave(taskId, attempt + 1)
            return
          }
        } catch { /* re-fetch failed — fall through to the conflict path */ }
      }
      // Genuine same-column conflict, retry cap hit, or re-fetch failed: the
      // existing non-destructive behaviour — advance the baseline from the 409
      // body (a later change re-saves cleanly), do NOT resubmit, warn via toast.
      if (resp.data?.currentUpdatedAt) setTaskBaseUpdatedAt(taskId, resp.data.currentUpdatedAt)
      // Only a genuine NON-collab conflict warns; an all-collab clash (just the
      // description mirror) resolves silently — Yjs owns the merged content.
      if (nonCollabTouched.length === 0) return
      useCasevoStore.getState().setSaveConflict(
        "This task was updated by someone else. Your latest change wasn't saved — please review and re-enter it.",
      )
      return
    }
    // eslint-disable-next-line no-console
    console.error('[TaskAutoSave] Failed to save task:', taskId, err)
    // ★ (Audit) Don't DROP the edit (it was removed from pending before the
    // request) — same recovery as the witness/deadline hooks: 404 = row gone,
    // drop quietly; 4xx = server rejected the value, surface the error but
    // don't re-queue; network/5xx = re-queue UNDER any newer edit, surface the
    // error, and auto-retry once.
    const status = (err as { response?: { status?: number } }).response?.status
    if (status === 404) {
      if (taskErrored.delete(taskId) && taskErrored.size === 0) useCasevoStore.getState().setSaveError(null)
      return
    }
    useCasevoStore.getState().setSaveError(err instanceof Error ? err : new Error('Save failed'))
    taskErrored.add(taskId)
    if (status !== undefined && status >= 400 && status < 500) return
    pendingPayloads.set(taskId, { ...payload, ...(pendingPayloads.get(taskId) ?? {}) })
    if (attempt === 0) {
      setTimeout(() => { void runTaskSave(taskId, 1) }, 5000)
    }
  }
}

// ── Stage 1e: apply a REMOTE task edit without a save-loop ────────────
// Counterpart to applyRemoteWitnessEdit. This hook has no store subscription
// (saves fire only via scheduleTaskSave from the form's onChange), so writing
// the tasks[] cache directly triggers no save — no loop, no baseline lockstep.
// We: (1) merge the remote field(s) into the specific task in the store's flat
// tasks[] cache, and (2) advance THAT task's optimistic-concurrency base to the
// server updatedAt the sender's save produced.
//
// This updates the STORE + base only. The open edit form holds field values in
// LOCAL state (formTitle etc.), not the store — so the caller (TasksView's
// receive subscription) must ALSO call the form setters when the edited task is
// the one currently open, so the visible form reflects the remote change.
// No-ops if the task isn't in the loaded list.
export const applyRemoteTaskEdit = (
  taskId: string,
  updates: Partial<Task>,
  serverUpdatedAt: string,
): void => {
  const list = useCasevoStore.getState().tasks
  if (!list) return
  const row = list.find((t) => t.id === taskId)
  if (!row) return
  // Pure store merge (no API call, no scheduleTaskSave) → no loop. ALSO refresh
  // the row's updatedAt to the server value so a later form-open re-seed reads
  // a FRESH base (not the stale pre-edit value) — this is what fixes the
  // "works once, 409 on reopen" bug for receivers.
  const merged = { ...row, ...updates, updatedAt: serverUpdatedAt }
  useCasevoStore.setState({
    tasks: list.map((t) => (t.id === taskId ? merged : t)),
  })
  setTaskBaseUpdatedAt(taskId, serverUpdatedAt)
  // Keep the column-aware base VALUES aligned with the applied remote state.
  setTaskBaseValues(taskId, merged as unknown as Record<string, unknown>)
}

export const scheduleTaskSave = (
  taskId: string,
  changes: Partial<CreateTaskPayload>,
): void => {
  // Merge with any partial already queued so several field changes collapse
  // into one PATCH.
  const existing = pendingPayloads.get(taskId) ?? {}
  pendingPayloads.set(taskId, { ...existing, ...changes })
  // Reset the debounce window.
  const existingTimer = timers.get(taskId)
  if (existingTimer !== undefined) clearTimeout(existingTimer)
  const newTimer = setTimeout(() => {
    timers.delete(taskId)
    void runTaskSave(taskId)
  }, DEBOUNCE_MS)
  timers.set(taskId, newTimer)
}

// Cancel pending timer + payload for one task. Used on delete so we don't
// PATCH a row that's about to vanish from the backend.
export const cancelTaskSave = (taskId: string): void => {
  const timer = timers.get(taskId)
  if (timer !== undefined) {
    clearTimeout(timer)
    timers.delete(taskId)
  }
  pendingPayloads.delete(taskId)
}

// Fire every pending task save immediately and await them all. Mirrors
// flushWitnessAutoSave. Call before navigating away from the edit form (the
// "Save changes" button flushes through this).
export const flushTaskAutoSave = async (): Promise<void> => {
  const ids = Array.from(timers.keys())
  for (const id of ids) {
    const timer = timers.get(id)
    if (timer !== undefined) clearTimeout(timer)
    timers.delete(id)
  }
  await Promise.all(ids.map((id) => runTaskSave(id)))
}

// beforeunload guard — mirrors the other autosaves so a refresh / tab close
// mid-edit prompts the user when there's a pending task edit. No-op when
// nothing's pending.
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', (event: BeforeUnloadEvent) => {
    if (timers.size === 0 && pendingPayloads.size === 0) return
    event.preventDefault()
    event.returnValue = ''
  })
}
